import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const COUNCILS = {
  'city-of-sydney': {
    name: 'City of Sydney',
    state: 'NSW',
    intro: 'The City of Sydney is one of the most active development approval areas in Australia, processing hundreds of DAs annually for high-rise residential, commercial towers, mixed-use precincts, student accommodation, and heritage conversions. Their Sydney Development Control Plan 2012 (Sydney DCP 2012) contains detailed waste management requirements under Section 3.14, which every WMP must address specifically.',
    overview: 'The City of Sydney requires Waste Management Plans for virtually all development applications. Their requirements are among the most detailed of any NSW council, reflecting the high-density urban environment and the council\'s ambitious sustainability targets under Sustainable Sydney 2030-2050. The council operates its own waste collection service for residential buildings up to a certain threshold, after which developments must engage private contractors.',
    dcpReference: 'Sydney DCP 2012, Section 3.14 — Waste',
    requirements: [
      'Operational Waste Management Plan required for all DAs with 3 or more dwellings',
      'Demolition & Construction WMP required for any project involving demolition or significant construction',
      'Waste storage areas must comply with City of Sydney DCP 2012 Section 3.14',
      'Minimum bin room sizing: calculated based on waste generation rates × collection frequency, plus access clearances',
      'Collection vehicle access assessed against AS 2890.2 — swept path analysis required for all off-street collection',
      'Waste stream separation: general waste, commingled recycling, FOGO (food organics), and bulky waste',
      'Bin presentation arrangements must be detailed — how bins move from storage to collection point',
      'Waste signage plan required showing location and content of all waste education materials',
      'Ventilation: mechanical exhaust ventilation required for all enclosed bin rooms',
      'Washdown facilities: hose cock, floor waste, and graded flooring with bunded entry',
    ],
    wasteRates: [
      { type: '1-bedroom apartment', general: '60L/unit/wk', recycling: '30L/unit/wk', fogo: '7L/unit/wk' },
      { type: '2-bedroom apartment', general: '80L/unit/wk', recycling: '40L/unit/wk', fogo: '7L/unit/wk' },
      { type: '3-bedroom apartment', general: '100L/unit/wk', recycling: '60L/unit/wk', fogo: '10L/unit/wk' },
      { type: 'Commercial office', general: '10L/100m²/day', recycling: '10L/100m²/day', fogo: '5L/100m²/day' },
      { type: 'Retail', general: '20L/100m²/day', recycling: '10L/100m²/day', fogo: '10L/100m²/day' },
      { type: 'Restaurant/café', general: '25L/100m²/day', recycling: '10L/100m²/day', fogo: '20L/100m²/day' },
    ],
    commonIssues: [
      { title: 'Bin room too small', desc: 'The most common rejection reason. City of Sydney calculates required bin numbers based on their specific generation rates and collection frequency. Architects often underestimate the required floor area, particularly when accounting for access aisles, door clearances, and equipment like bin lifters or compactors.' },
      { title: 'No swept path for collection vehicle', desc: 'Any development with off-street waste collection must include a swept path analysis showing the nominated collection vehicle (typically SRV 6.4m or MRV 8.8m) can enter, service, and exit the site in a forward direction. This is frequently missing from initial submissions.' },
      { title: 'Missing FOGO stream', desc: 'City of Sydney now expects a food organics waste stream for residential developments. WMPs that only include general waste and recycling will be sent back for amendment.' },
      { title: 'Bin presentation not detailed', desc: 'Council wants to see exactly how bins will be transferred from the storage room to the collection point — distances, routes, timing, and who is responsible. A simple "bins will be placed at the kerb" is insufficient.' },
      { title: 'Incorrect collection arrangements', desc: 'Developments above the council collection threshold must use private contractors, but the WMP must still comply with council requirements regarding collection times, vehicle types, and noise restrictions.' },
    ],
    projectTypes: ['High-rise residential towers', 'Commercial office buildings', 'Mixed-use (residential + retail)', 'Student accommodation', 'Heritage conversions and adaptive reuse', 'Hotels and serviced apartments', 'Retail and hospitality precincts', 'Government and institutional buildings'],
    suburbs: ['Sydney CBD', 'Pyrmont', 'Ultimo', 'Surry Hills', 'Darlinghurst', 'Woolloomooloo', 'Potts Point', 'Redfern', 'Waterloo', 'Zetland', 'Rosebery', 'Alexandria', 'Erskineville', 'Newtown', 'Glebe', 'Forest Lodge', 'Chippendale', 'Haymarket', 'Barangaroo'],
  },
  'canterbury-bankstown': {
    name: 'Canterbury-Bankstown',
    state: 'NSW',
    intro: 'Canterbury-Bankstown Council is one of the largest local government areas in Sydney by population. Their DCP contains detailed waste management requirements, particularly for multi-unit residential developments in high-growth areas like Bankstown, Campsie, and Canterbury.',
    overview: 'Canterbury-Bankstown Council processes a high volume of development applications across a diverse urban landscape. Their waste management DCP controls address both medium-density infill developments and larger apartment complexes in town centre renewal areas. The council operates its own residential waste collection service but requires private collection for larger commercial developments.',
    dcpReference: 'Canterbury-Bankstown DCP, Part B11 — Waste Management',
    requirements: [
      'Operational WMP required for all residential developments of 3 or more dwellings',
      'D&CWMP required for all demolition and construction phases',
      'Specific waste generation rates per dwelling type as per Canterbury-Bankstown DCP Part B11',
      'Waste storage room minimum sizing requirements including access clearances and ventilation',
      'Bin compactor requirements for developments exceeding 50 residential units',
      'Collection vehicle swept path analysis for all internal driveways and loading areas',
      'Waste stream separation: general waste, commingled recycling, garden organics, bulky waste',
      'Chute systems required for residential buildings of 4+ storeys with compaction at base',
    ],
    wasteRates: [
      { type: '1-bed apartment', general: '70L/unit/wk', recycling: '35L/unit/wk', fogo: '—' },
      { type: '2-bed apartment', general: '100L/unit/wk', recycling: '50L/unit/wk', fogo: '—' },
      { type: '3-bed apartment', general: '120L/unit/wk', recycling: '60L/unit/wk', fogo: '—' },
      { type: 'Townhouse', general: '140L/unit/wk', recycling: '80L/unit/wk', fogo: '40L/unit/wk' },
    ],
    commonIssues: [
      { title: 'No compactor for large developments', desc: 'Canterbury-Bankstown requires bin compactors for developments over 50 units. WMPs that don\'t include compaction equipment for large projects will be returned.' },
      { title: 'Insufficient chute system detail', desc: 'Buildings 4+ storeys require waste chutes with hopper rooms on each level. The WMP must detail chute diameter, hopper room dimensions, and compaction equipment at the base.' },
    ],
    projectTypes: ['Multi-unit residential apartments', 'Townhouse developments', 'Mixed-use (residential above retail)', 'Industrial and warehouse conversions', 'Aged care facilities', 'Community and educational facilities'],
    suburbs: ['Bankstown', 'Campsie', 'Canterbury', 'Lakemba', 'Punchbowl', 'Revesby', 'Padstow', 'Roselands', 'Bass Hill', 'Belmore'],
  },
  'waverley': {
    name: 'Waverley',
    state: 'NSW',
    intro: 'Waverley Council covers the eastern suburbs including Bondi, Bronte, and surrounding areas. Their waste management requirements reflect the high-density urban environment, narrow streets, and limited vehicular access typical of the eastern suburbs.',
    overview: 'Waverley Council\'s DCP contains specific waste provisions that address the unique challenges of the eastern suburbs — tight sites, narrow laneways, heritage overlays, and limited on-street parking for collection vehicles. Many developments in Waverley require creative waste solutions due to site access constraints.',
    dcpReference: 'Waverley DCP 2012, Part B9 — Waste',
    requirements: [
      'Operational WMP required for all DAs with multiple dwellings',
      'D&CWMP required for all demolition and construction projects',
      'Waste storage rooms must be at ground level or basement with grade access for bin movement',
      'Collection vehicle access assessment — many streets have restricted truck access',
      'FOGO stream required for all new residential developments',
      'Bulky waste storage area requirements (separate from bin room)',
      'Noise management for early morning collections in residential areas',
    ],
    wasteRates: [
      { type: '1-bed apartment', general: '60L/unit/wk', recycling: '30L/unit/wk', fogo: '10L/unit/wk' },
      { type: '2-bed apartment', general: '80L/unit/wk', recycling: '40L/unit/wk', fogo: '10L/unit/wk' },
      { type: '3-bed apartment', general: '100L/unit/wk', recycling: '50L/unit/wk', fogo: '15L/unit/wk' },
    ],
    commonIssues: [
      { title: 'Vehicle access constraints', desc: 'Many Waverley streets cannot accommodate standard waste collection vehicles. WMPs must assess actual street widths and turning capabilities. Some sites require smaller vehicles or on-street collection only.' },
      { title: 'Heritage site restrictions', desc: 'Heritage conservation areas limit where waste rooms can be located and how they\'re ventilated. External louvres and roller doors may require heritage approval.' },
    ],
    projectTypes: ['Beachside apartments', 'Boutique residential', 'Mixed-use retail/residential', 'Heritage conversions', 'Boarding houses', 'Commercial renovations'],
    suburbs: ['Bondi', 'Bondi Beach', 'Bondi Junction', 'Bronte', 'Tamarama', 'Waverley', 'Queens Park', 'Vaucluse', 'Dover Heights'],
  },
  'blacktown': {
    name: 'Blacktown',
    state: 'NSW',
    intro: 'Blacktown City Council is one of the largest LGAs in NSW by population, covering established suburbs and major growth areas in Western Sydney. Their waste management DCP controls cover greenfield subdivisions, medium-density infill, and large apartment complexes.',
    overview: 'Blacktown\'s rapid growth means a high volume of DAs for both greenfield developments in new release areas and urban renewal projects in established centres. Their waste management requirements address the full spectrum from detached housing subdivisions to high-density apartment towers in Blacktown CBD.',
    dcpReference: 'Blacktown DCP 2015, Section 6.2 — Waste Management',
    requirements: [
      'Operational WMP required for all residential developments of 3 or more dwellings',
      'D&CWMP required for all demolition and new construction',
      'Waste generation rates as per Blacktown DCP Section 6.2',
      'Waste storage room sizing to accommodate 3-stream collection minimum',
      'Collection access requirements for council collection vehicles (typically MRV 8.8m)',
      'Bin presentation area design — covered, level surface, within 15m of collection point',
    ],
    wasteRates: [
      { type: '1-bed apartment', general: '70L/unit/wk', recycling: '35L/unit/wk', fogo: '—' },
      { type: '2-bed apartment', general: '90L/unit/wk', recycling: '45L/unit/wk', fogo: '—' },
      { type: '3-bed apartment', general: '120L/unit/wk', recycling: '60L/unit/wk', fogo: '—' },
      { type: 'Detached dwelling', general: '140L/unit/wk', recycling: '80L/unit/wk', fogo: '40L/unit/wk' },
    ],
    commonIssues: [
      { title: 'Greenfield vs infill requirements', desc: 'New release areas have different waste servicing arrangements to established suburbs. WMPs must specify the correct collection service type for the location.' },
    ],
    projectTypes: ['Master-planned communities', 'Multi-unit residential', 'Greenfield subdivisions', 'Industrial and logistics', 'Retail centres', 'Community and educational facilities'],
    suburbs: ['Blacktown', 'Mount Druitt', 'Marsden Park', 'Schofields', 'Riverstone', 'Seven Hills', 'Rooty Hill', 'Quakers Hill'],
  },
  'the-hills': {
    name: 'The Hills Shire',
    state: 'NSW',
    intro: 'The Hills Shire Council covers the rapidly developing North-West Sydney growth corridor. Their waste management requirements address both established suburban areas and new release precincts with different servicing arrangements around the new Metro stations.',
    overview: 'The Hills Shire is experiencing significant development activity around the Sydney Metro Northwest corridor — Box Hill, North Kellyville, and Rouse Hill are seeing high-density apartment developments alongside traditional suburban housing. Council\'s waste DCP reflects this mix with different requirements for different development types and locations.',
    dcpReference: 'The Hills DCP 2012, Part C Section 6 — Waste Management',
    requirements: [
      'Operational WMP required for all multi-dwelling developments',
      'D&CWMP required for all demolition and construction phases',
      'Different requirements for developments in new release areas vs established suburbs',
      'Waste generation rates per The Hills DCP Part C Section 6',
      'Private collection arrangements required for developments exceeding 50 units',
      'FOGO provision required for all new residential developments',
    ],
    wasteRates: [
      { type: '1-bed apartment', general: '70L/unit/wk', recycling: '35L/unit/wk', fogo: '10L/unit/wk' },
      { type: '2-bed apartment', general: '100L/unit/wk', recycling: '50L/unit/wk', fogo: '15L/unit/wk' },
      { type: 'Townhouse', general: '140L/unit/wk', recycling: '70L/unit/wk', fogo: '40L/unit/wk' },
    ],
    commonIssues: [
      { title: 'Metro corridor requirements', desc: 'Developments near Metro stations have specific waste handling requirements related to the underground infrastructure. WMPs must consider access restrictions during and after Metro construction.' },
    ],
    projectTypes: ['Residential apartments near Metro', 'Townhouse estates', 'Mixed-use town centres', 'Retail and commercial', 'Schools and community facilities', 'Aged care'],
    suburbs: ['Castle Hill', 'Baulkham Hills', 'Rouse Hill', 'Box Hill', 'North Kellyville', 'Norwest', 'Bella Vista', 'Glenhaven'],
  },
  'central-coast': {
    name: 'Central Coast',
    state: 'NSW',
    intro: 'Central Coast Council covers the Gosford and Wyong areas north of Sydney. Their waste management requirements serve a mix of coastal, suburban, and semi-rural developments with varying collection service arrangements across this large and diverse LGA.',
    overview: 'Central Coast Council manages waste services across a diverse geographic area from high-density Gosford CBD developments to rural-residential properties. Their DCP waste provisions reflect this diversity, with different requirements depending on location and development type. The council operates both residential and commercial waste collection services.',
    dcpReference: 'Central Coast DCP, Chapter 2.16 — Waste Management',
    requirements: [
      'Operational WMP required for all DAs with multiple residential units',
      'D&CWMP required for all demolition and construction projects',
      'Waste generation rates as per Central Coast DCP Chapter 2.16',
      'Consideration of council collection vs private collection based on development size',
      'Collection vehicle access for both standard suburban and larger collection vehicles',
      'Waste storage area ventilation and washdown requirements',
      'Bin presentation area within 3m of kerb line for kerbside collection developments',
    ],
    wasteRates: [
      { type: '1-bed apartment', general: '60L/unit/wk', recycling: '30L/unit/wk', fogo: '—' },
      { type: '2-bed apartment', general: '80L/unit/wk', recycling: '40L/unit/wk', fogo: '—' },
      { type: '3-bed apartment', general: '100L/unit/wk', recycling: '50L/unit/wk', fogo: '—' },
    ],
    commonIssues: [
      { title: 'Council vs private collection confusion', desc: 'Central Coast has different thresholds for council collection eligibility. WMPs must correctly identify whether the development qualifies for council collection or must engage private contractors.' },
      { title: 'Coastal site constraints', desc: 'Developments on steep coastal sites face challenges with collection vehicle access and bin room location. WMPs must address grade limitations and alternative servicing arrangements.' },
    ],
    projectTypes: ['Coastal residential developments', 'Multi-unit apartments (Gosford CBD)', 'Commercial and retail', 'Tourist accommodation', 'Industrial estates', 'Rural-residential subdivisions'],
    suburbs: ['Gosford', 'Terrigal', 'Erina', 'Tuggerah', 'Wyong', 'The Entrance', 'Woy Woy', 'Umina Beach', 'Avoca Beach'],
  },
};

export default function CouncilPage() {
  const { slug } = useParams();
  const council = COUNCILS[slug];

  if (!council) {
    return (
      <PublicLayout>
        <div className="py-20 text-center">
          <h1 className="text-2xl font-bold text-[#0A2540]">Council not found</h1>
          <Link to="/services" className="text-blue-600 mt-4 inline-block">View our services</Link>
        </div>
      </PublicLayout>
    );
  }

  const faqItems = [
    { q: `Do I need a Waste Management Plan for a DA in ${council.name}?`, a: `Most developments of 3 or more dwellings in ${council.name} require an Operational Waste Management Plan as part of the DA submission. Commercial and mixed-use developments also typically require a WMP. Check ${council.dcpReference} for the specific thresholds that apply to your project.` },
    { q: `How long does it take to prepare a WMP for ${council.name}?`, a: `Standard Operational WMPs for ${council.name} are typically delivered within 5 business days of receiving architectural plans and project details. Complex projects (SSDA, Green Star, multi-stage) may take 2-3 weeks.` },
    { q: `What if ${council.name} rejects my WMP?`, a: `We regularly assist with WMP revisions following council feedback. Send us the council\'s Request for Further Information (RFI) and your current WMP — we can typically resolve issues within 3-5 business days. Common rejection reasons for ${council.name} include incorrect waste generation rates, undersized bin rooms, and missing waste stream provisions.` },
  ];

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  });

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json">{faqSchema}</script>
      </Helmet>
      <SEO
        title={`Waste Management Plan ${council.name} | WMP Consultant ${council.state}`}
        description={`Specialist waste management plan consultant for ${council.name} developments. Council-compliant Operational WMPs and D&CWMPs prepared by experienced consultants. ${council.dcpReference}. Fast turnaround.`}
        keywords={`waste management plan ${council.name}, WMP ${council.name}, waste management consultant ${council.name}, ${council.name} council WMP, ${council.name} development application waste, ${council.name} DCP waste`}
        canonicalPath={`/council/${slug}`}
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
              <Link to="/" className="hover:text-blue-600">Home</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-blue-600">Services</Link>
              <span>/</span>
              <span className="text-slate-600">{council.name}</span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4">
              Waste Management Plans for {council.name}
            </h1>
            <p className="text-base text-slate-600 leading-relaxed mb-4">{council.intro}</p>
            {council.overview && <p className="text-sm text-slate-500 leading-relaxed">{council.overview}</p>}
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/contact" className="px-7 py-3 rounded-full bg-[#0A2540] text-white font-medium text-sm hover:bg-black transition-colors shadow-lg shadow-slate-900/10">
                Request a Quote
              </Link>
              <a href="tel:0431469974" className="px-7 py-3 rounded-full border border-slate-200 text-[#0A2540] font-medium text-sm hover:bg-slate-50 transition-colors">
                Call 0431 469 974
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DCP Reference + Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10">
            <div>
              {council.dcpReference && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-4 border border-blue-100">
                  {council.dcpReference}
                </div>
              )}
              <h2 className="text-2xl font-bold text-[#0A2540] mb-4">{council.name} WMP Requirements</h2>
              <ul className="space-y-3">
                {council.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
                <h3 className="text-sm font-bold text-[#0A2540] mb-4">Common Project Types</h3>
                <div className="space-y-2">
                  {council.projectTypes.map((p, i) => (
                    <div key={i} className="text-sm text-slate-600 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                      {p}
                    </div>
                  ))}
                </div>
              </div>
              {council.suburbs && (
                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6">
                  <h3 className="text-sm font-bold text-[#0A2540] mb-4">Suburbs We Cover in {council.name}</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {council.suburbs.map((s, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Waste Generation Rates */}
      {council.wasteRates && (
        <section className="py-16 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4">{council.name} Waste Generation Rates</h2>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed max-w-2xl">
              The following waste generation rates are indicative of {council.name}&apos;s requirements. Always confirm with the current version of {council.dcpReference} as rates are periodically updated.
            </p>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-slate-700">Development Type</th>
                    <th className="px-5 py-3 text-left font-medium text-slate-700">General Waste</th>
                    <th className="px-5 py-3 text-left font-medium text-slate-700">Recycling</th>
                    <th className="px-5 py-3 text-left font-medium text-slate-700">FOGO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {council.wasteRates.map((r, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3 font-medium text-slate-700">{r.type}</td>
                      <td className="px-5 py-3 text-slate-600">{r.general}</td>
                      <td className="px-5 py-3 text-slate-600">{r.recycling}</td>
                      <td className="px-5 py-3 text-slate-600">{r.fogo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">Note: Rates are indicative and subject to council updates. Contact us for current confirmed rates.</p>
          </div>
        </section>
      )}

      {/* Common Issues */}
      {council.commonIssues && council.commonIssues.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Common WMP Issues in {council.name}</h2>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed max-w-2xl">
              Based on our experience preparing WMPs for {council.name}, these are the most common reasons plans are returned for amendment:
            </p>
            <div className="space-y-3 max-w-3xl">
              {council.commonIssues.map((issue, i) => (
                <div key={i} className="bg-slate-50 rounded-xl border border-slate-100 p-5">
                  <h3 className="text-sm font-bold text-[#0A2540] mb-1">{issue.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{issue.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Experience */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0A2540] mb-4">Our Experience in {council.name}</h2>
          <p className="text-sm text-slate-600 leading-relaxed max-w-2xl mb-4">
            PlanGen Consulting has delivered waste management plans for developments across {council.name}. Our principal consultant Nathan Smith has 8+ years of specialist experience and understands the specific DCP requirements, waste generation rates, and submission expectations for this council area.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed max-w-2xl mb-6">
            We prepare Operational WMPs, Demolition &amp; Construction WMPs, Construction Management Plans, and Green Star waste credit documentation for {council.name} developments of all scales — from boutique residential to major commercial projects.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/projects" className="text-sm font-medium text-blue-600 hover:text-blue-700">View our projects &rarr;</Link>
            <Link to="/about" className="text-sm font-medium text-blue-600 hover:text-blue-700">About the consultant &rarr;</Link>
            <Link to="/services" className="text-sm font-medium text-blue-600 hover:text-blue-700">All services &rarr;</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Frequently Asked Questions — {council.name}</h2>
          <div className="space-y-4">
            {faqItems.map((faq, i) => (
              <details key={i} className="group bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-semibold text-[#0A2540] pr-4">{faq.q}</span>
                  <svg className="w-4 h-4 text-slate-400 flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </summary>
                <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Need a WMP for {council.name}?</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Send us your plans and project details. We&apos;ll provide a tailored quote within 24 hours.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/contact" className="inline-flex px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg">
              Request a Quote
            </Link>
            <a href="tel:0431469974" className="inline-flex px-8 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-colors">
              Call 0431 469 974
            </a>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="py-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3">Related Pages</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/waste-management-plan-nsw" className="text-sm text-blue-600 hover:text-blue-800 font-medium">WMP for NSW &rarr;</Link>
            <Link to="/waste-management-plan-vic" className="text-sm text-blue-600 hover:text-blue-800 font-medium">WMP for VIC &rarr;</Link>
            <Link to="/services" className="text-sm text-blue-600 hover:text-blue-800 font-medium">All Services &rarr;</Link>
            <Link to="/projects" className="text-sm text-blue-600 hover:text-blue-800 font-medium">Our Projects &rarr;</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
